import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { Button, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IoIosArrowBack, IoIosArrowRoundBack, IoIosArrowRoundForward } from 'react-icons/io';
import { Link } from 'react-router-dom';

import EquipmentPageLayout from '../components/EquipmentPageLayout';
import Title from '../../../components/Title';
import useDeviceSettings from '../hooks/useDeviceSettings';
import { getDevice } from '../selectors';
import { isFitPower } from '../utils';
import {
  FormCheck,
  FormControlDropdown,
  FormControlNumber,
  FormControlText,
  FormLabel,
  FormText,
  Heading1,
  Heading2,
  NewSection,
  Paragraph,
  PublicImage,
} from './EquipmentSettings';

const formatTime = (time) => {
  const timeFormat = 'DD MMM YYYY h:mm:ss A';
  return moment.unix(time).format(timeFormat);
};

function EquipmentSettingsPrevious() {
  const { t } = useTranslation();
  const { id } = useParams();
  const deviceId = Number(id);
  const device = useSelector(state => getDevice(state, deviceId));
  const {
    loadingPrevious,
    lastFetchPrevious,
    errorPrevious,
    previousSettings = [],
  } = useDeviceSettings(deviceId, { fetchPrevious: true });
  const [settings, setSettings] = useState(null);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  // index 0 is the most recent
  const [settingsIndex, setSettingsIndex] = useState(0);
  const [previousDisabled, setPreviousDisabled] = useState(false);
  const [nextDisabled, setNextDisabled] = useState(true);

  useEffect(() => {
    if (previousSettings[0]?.settings) {
      setSettings(previousSettings[0].settings);
      setValidFrom(formatTime(previousSettings[0].valid_from));
      setValidTo(formatTime(previousSettings[0].valid_to));
    }
  }, [previousSettings]);

  const defaultValues = useMemo(() => {
    const values = {};
    if (settings) {
      for(const key of Object.keys(settings)) {
        values[key] = settings[key].value;
      }
    }
    return values;
  }, [settings]);

  const { register, reset } = useForm({
    mode: 'onSubmit',
    defaultValues
  });

  useEffect(() => {
    // length === 0 doesn't matter because the buttons won't be visible.
    if (previousSettings?.length === 1) {
      setPreviousDisabled(true);
      setNextDisabled(true);
    } else if (previousSettings?.length > 1) {
      setPreviousDisabled(false);
      setNextDisabled(true);
    }
  }, [previousSettings]);

  useEffect(() => {
    if(defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  const onIterate = (button) => {
    // Button will already be disabled, so this is just being extra careful in case some UI changes.
    if (previousSettings?.length < 2) return;
    const lengthSettings = previousSettings.length;
    let newIndex = settingsIndex;
    if (button === 'previous') newIndex += 1;
    else newIndex -= 1;
    if (lengthSettings - 1 === newIndex) {
      setPreviousDisabled(true);
      setNextDisabled(false);
    } else if (newIndex === 0) {
      setPreviousDisabled(false);
      setNextDisabled(true);
    }
    else {
      setPreviousDisabled(false);
      setNextDisabled(false);
    }
    const iterateSettings = previousSettings[newIndex].settings;
    const iterateValidFrom = previousSettings[newIndex].valid_from;
    const iterateValidTo = previousSettings[newIndex].valid_to;
    setSettings(iterateSettings);
    setValidFrom(formatTime(iterateValidFrom));
    setValidTo(formatTime(iterateValidTo));
    setSettingsIndex(newIndex);
  };

  return (
    <EquipmentPageLayout>
      <div className="my-4 mx-3">
        <Title
          title={t('screens.equipment.equipment-settings-previous.title')}
          loading={loadingPrevious}
          lastFetch={lastFetchPrevious}
          error={errorPrevious}
        />
        {!isFitPower(device) && <div>{t('screens.equipment.equipment-settings-previous.previous-only-fitpower')}</div>}
        {isFitPower(device) &&
        <>
          <p>{t('screens.equipment.equipment-settings-previous.view-all-previous-settings')}</p>
          {settings ? <>
            <Button
              variant="outline-dark"
              type="submit"
              size="md"
              disabled={loadingPrevious || previousDisabled}
              onClick={() => onIterate('previous')}
            ><IoIosArrowRoundBack /><span>{t('screens.equipment.equipment-settings-previous.previous')}</span>
            </Button>
            &nbsp;
            <Button
              variant="outline-dark"
              type="submit"
              size="md"
              disabled={loadingPrevious || nextDisabled}
              onClick={() => onIterate('next')}
            ><span>{t('screens.equipment.equipment-settings-previous.next')}</span> <IoIosArrowRoundForward />
            </Button>
            <div><strong>{t('screens.equipment.equipment-settings-previous.valid-from', { validFrom: validFrom })}</strong></div>
            <div><strong>{t('screens.equipment.equipment-settings-previous.valid-to', { validTo: validTo })}</strong></div>
            &nbsp;
          </> : <p>{t('screens.equipment.equipment-settings-previous.no-previous-found')}</p>}
          <>
            <div>
              <Link to={`/equipment/${device.id}/settings`}>
                <Button
                  variant="outline-dark"
                  type="submit"
                  size="md"
                  disabled={loadingPrevious}
                ><IoIosArrowBack />
                  <span>
                    {t('screens.equipment.equipment-settings-previous.back-to-current-settings')}
                  </span>
                </Button>
              </Link>
            </div>
          </>
          &nbsp;
          {NewSection()}
          {/* No onSubmit because the Form is read-only. */}
          {settings && <Form>
            {
              Object.keys(settings).map(key => {
                const {label, description, value_type, value, new_section} = settings[key];
                if (value_type === 'heading1') {
                  return Heading1(key, label, description);
                }
                if (value_type === 'heading2') {
                  return Heading2(key, label, description);
                }
                if (value_type === 'paragraph') {
                  return Paragraph(key, description);
                }
                return (
                  <div key={key}>
                    {new_section && NewSection()}
                    <Form.Group as={Row} controlId={key}>
                      {FormLabel(label)}
                      <Col sm="9">
                        {value_type === 'bool' ? FormCheck(register, key, true) :
                          value_type === 'number' ? FormControlNumber(register, key, true) :
                            value_type === 'image' ? PublicImage(value) :
                              value_type === 'dropdown' ? FormControlDropdown(register, key, value, true) :
                                value_type === 'hidden' ? null :
                                  FormControlText(register, key, true)
                        }
                        {FormText(description)}
                      </Col>
                    </Form.Group>
                  </div>
                );
              })
            }
          </Form>}
        </>}
      </div>
    </EquipmentPageLayout>
  );
}

export default EquipmentSettingsPrevious;
